import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import type { User } from "@supabase/supabase-js";
import type { Tenant } from "@/lib/types";
import { waitlistService, cardRequestService } from "@/services";
import { supabase } from "@/integrations/supabase/client";
import CardMockPreview from "@/components/CardMockPreview";
import { Button } from "@/components/ui/button";
import { Share2, Copy, Users } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { shareCard } from "@/lib/share-card";

interface Props {
  tenant: Tenant;
  user: User;
  onStartKyc: () => void;
}

const UserHome = ({ tenant, user, onStartKyc }: Props) => {
  const { tenant_slug } = useParams();
  const { toast } = useToast();
  const cardRef = useRef<HTMLDivElement>(null);
  const [waitlistInfo, setWaitlistInfo] = useState<any>(null);
  const [cardRequest, setCardRequest] = useState<any>(null);
  const [selectedAvatar, setSelectedAvatar] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [wl, cr] = await Promise.all([
        waitlistService.getUserWaitlistEntry(tenant.id, user.id),
        cardRequestService.getUserLatestCardRequest(user.id, tenant.id),
      ]);
      setWaitlistInfo(wl.data);
      const cardData = cr.data;
      setCardRequest(cardData);

      // Try to load selected avatar, fallback to latest generation for this card
      if (cardData?.selected_avatar_image_id) {
        const { data: av } = await supabase
          .from("avatar_generations")
          .select("id, generated_image_url")
          .eq("id", cardData.selected_avatar_image_id)
          .maybeSingle();
        setSelectedAvatar(av);
      } else if (cardData?.id) {
        // Fallback: get latest completed generation for this card request
        const { data: av } = await supabase
          .from("avatar_generations")
          .select("id, generated_image_url")
          .eq("card_request_id", cardData.id)
          .eq("status", "completed")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        setSelectedAvatar(av);
      }
      setLoading(false);
    };
    load();
  }, [tenant.id, user.id]);

  const formatCents = (cents: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);

  // P7.2 · the composite + delivery logic lives in src/lib/share-card.ts and is
  // shared with the consumer funnel (S5Reveal). Do not inline it here again.
  const handleShare = async () => {
    setSharing(true);
    const code = waitlistInfo?.referral_code;
    const shareUrl = `${window.location.origin}/t/${tenant_slug}${code ? `?ref=${code}` : ""}`;
    const shareText = `Use my link to get your personalised ${tenant.brandName} card!`;

    const outcome = await shareCard({
      channel: "os",
      shareUrl,
      text: shareText,
      title: `My ${tenant.brandName} Card`,
      composite: cardRef.current
        ? {
            node: cardRef.current,
            brandLine: tenant.brandName,
          }
        : null,
    });

    if (outcome === "downloaded_copied") {
      toast({ title: "Image saved", description: "Downloaded the image and copied your link." });
    } else if (outcome === "copied") {
      toast({ title: "Copied!", description: "Share text & link copied to clipboard." });
    } else if (outcome === "failed") {
      toast({ title: "Couldn't share", description: "Please try again.", variant: "destructive" });
    }
    setSharing(false);
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const isWaitlist = tenant.tenantType === "waitlist";
  const earnings = waitlistInfo?.referral_earnings_cents || 0;
  const position = waitlistInfo?.position;
  const referralCode = waitlistInfo?.referral_code;
  const referralCount = waitlistInfo?.referral_count || 0;

  return (
    <div className="p-4 space-y-5 max-w-lg mx-auto animate-fade-in">
      {/* Welcome */}
      <div className="space-y-0.5">
        <h1 className="text-lg font-bold font-display">
          Welcome{cardRequest?.first_name || waitlistInfo?.first_name ? `, ${cardRequest?.first_name || waitlistInfo?.first_name}` : ""}! 👋
        </h1>
      </div>

      {/* Waitlist-only: Earnings */}
      {isWaitlist && (
        <div className="rounded-2xl p-6 text-center" style={{ background: `linear-gradient(135deg, hsl(${tenant.primaryColor}), hsl(${tenant.primaryColor} / 0.7))` }}>
          <p className="text-sm font-medium text-white/80">Earned from Referrals</p>
          <p className="text-4xl font-bold font-display text-white mt-1 tracking-tight">
            {formatCents(earnings)}
          </p>
          <p className="text-xs text-white/60 mt-2">{referralCount} friend{referralCount !== 1 ? "s" : ""} referred</p>
        </div>
      )}

      {/* Bank-only: Status banner */}
      {!isWaitlist && cardRequest && (
        <div className="rounded-2xl p-6 text-center" style={{ background: `linear-gradient(135deg, hsl(${tenant.primaryColor}), hsl(${tenant.primaryColor} / 0.7))` }}>
          <p className="text-sm font-medium text-white/80">Card Status</p>
          <p className="text-2xl font-bold font-display text-white mt-1 capitalize tracking-tight">
            {cardRequest.status}
          </p>
          {cardRequest.ref_number && (
            <p className="text-xs text-white/60 mt-2">Ref: {cardRequest.ref_number}</p>
          )}
        </div>
      )}

      {/* Waitlist stats */}
      {isWaitlist && (
        <div className="grid grid-cols-2 gap-3">
          {position && (
            <div className="rounded-xl border bg-card p-4 text-center shadow-sm">
              <Users className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
              <p className="text-2xl font-bold font-display">#{position}</p>
              <p className="text-[11px] text-muted-foreground">Waitlist Position</p>
            </div>
          )}
          {referralCode && (
            <button
              onClick={() => {
                navigator.clipboard.writeText(referralCode);
                toast({ title: "Copied!", description: "Referral code copied." });
              }}
              className="rounded-xl border bg-card p-4 text-center shadow-sm hover:bg-muted/50 transition-colors"
            >
              <Copy className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
              <p className="text-lg font-bold font-mono tracking-widest">{referralCode}</p>
              <p className="text-[11px] text-muted-foreground">Tap to copy code</p>
            </button>
          )}
        </div>
      )}

      {/* Share Button — waitlist only */}
      {isWaitlist && (
        <Button
          className="w-full gap-2 text-base"
          size="lg"
          disabled={sharing}
          style={{ backgroundColor: `hsl(${tenant.primaryColor})`, color: "white" }}
          onClick={handleShare}
        >
          <Share2 className="h-5 w-5" />
          {sharing ? "Preparing…" : "Share your card"}
        </Button>
      )}

      {/* Card Preview — always show if card request exists */}
      {cardRequest && (
        <div className="pt-2">
          <p className="text-sm font-semibold text-muted-foreground mb-3 text-center">Your Card Design</p>
          {/* Card ONLY — the composite paints the Paper ground, tagline and CTA. */}
          <div ref={cardRef} className="inline-block w-full max-w-xs mx-auto">
            <CardMockPreview
              cardColorHsl={cardRequest.card_color}
              orientation={cardRequest.orientation}
              cardLayout={tenant.cardLayout}
              avatarUrl={selectedAvatar?.generated_image_url}
              avatarPosition={cardRequest.avatar_position || { x: 50, y: 50 }}
              avatarScale={cardRequest.avatar_scale || 1}
              firstName={cardRequest.first_name}
              lastName={cardRequest.last_name}
              tenantName={tenant.brandName}
              cardNetwork={tenant.cardNetwork} showChip={tenant.showChip} showCardNetwork={tenant.showCardNetwork}
              backgroundUrl={cardRequest.background?.is_active ? cardRequest.background.image_url : null}
              className="w-full max-w-xs mx-auto"
            />
          </div>
          <p className="text-xs text-muted-foreground text-center mt-2">
            Status: <span className="capitalize font-medium text-foreground">{cardRequest.status}</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default UserHome;
